import React, { useRef } from 'react';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { Modal } from 'src/components/molecules/Modal/Modal';
import * as modalStyles from 'src/components/molecules/Modal/styles/ModalExternalStyles';
import { ModalLoader } from 'src/components/molecules/ModalLoader/ModalLoader';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { ThumbnailsContainer } from 'src/components/molecules/Thumbnails/ThumbnailsContainer';
import {
  IMAGES_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from 'src/components/organisms/ProductImagesCarousel/constants/elementIds';
import { mediaKey } from 'src/components/organisms/ProductImagesCarousel/media/buildProductMedia';
import type {
  ImagesLocation,
  MediaItem,
  NextOrPreviousPayload,
  ProductImagesState,
} from 'src/components/organisms/ProductImagesCarousel/types';
import { getIconPath } from 'src/utils/assetPaths';
import * as styles from './ProductImagesModal.styles';

const ICON_CLOSE = getIconPath('icons-24-close.svg');

const ZoomAndPanWrapper = dynamic(
  async () =>
    (await import(/* webpackChunkName: "ZoomAndPanWrapper" */ './ZoomAndPanWrapper'))
      .ZoomAndPanWrapper,
  { loading: () => <ModalLoader /> },
);

export interface ProductImagesModalStateProps {
  currentImage: ProductImagesState['currentImage'];
  isModalOpen: ProductImagesState['isModalOpen'];
  media: MediaItem[];
}

export interface ProductImagesModalDispatchProps {
  closeModal: VoidFunction;
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
}

type ProductImagesModalProps = ProductImagesModalStateProps & ProductImagesModalDispatchProps;

export const ProductImagesModal = ({
  closeModal,
  currentImage,
  isModalOpen,
  media,
  onNextImage,
  onPrevImage,
}: ProductImagesModalProps) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const totalImages = media.length;
  const location: ImagesLocation = 'Expanded image';

  return (
    <Modal closeModal={closeModal} contentLabel="View full screen image" isModalOpen={isModalOpen}>
      <div css={[modalStyles.modalViewport, styles.modalContentContainer]} ref={innerRef}>
        <div css={styles.imagesContainer}>
          {media.map((item, i) => (
            <div
              css={styles.tabpanel}
              id={`${IMAGES_ID_PREFIX}${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
              key={mediaKey(item)}
              role="tabpanel"
              style={i === currentImage ? {} : { display: 'none' }}
            >
              {item.type === 'video' ? (
                <ProductVideoSlide
                  contentPosition={i + 1}
                  isActive={currentImage === i}
                  location="Expanded image"
                  playbackId={item.playbackId}
                  posterUrl={item.posterUrl}
                  title={item.title}
                />
              ) : (
                <ZoomAndPanWrapper visible={currentImage === i}>
                  <Image
                    alt={`${item.title}${totalImages > 1 ? `, ${i + 1} of ${totalImages}` : ''}`}
                    css={styles.fullScreenImage}
                    loading="lazy"
                    src={getProductImageURL(item.href, 'original')}
                  />
                </ZoomAndPanWrapper>
              )}
            </div>
          ))}
          {totalImages > 1 && (
            <>
              <PreviousButton onClick={() => onPrevImage({ location, totalImages })} />
              <NextButton onClick={() => onNextImage({ location, totalImages })} />
              <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
            </>
          )}
        </div>
        {totalImages > 1 && (
          <div css={styles.thumbnails}>
            <ThumbnailsContainer location={location} />
          </div>
        )}
        <button css={styles.modalCloseButton} onClick={closeModal}>
          <img alt="Close" height={24} src={ICON_CLOSE} width={24} />
        </button>
      </div>
    </Modal>
  );
};
